import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  user: User | null;
  session: Session | null;
  userRole: string | null;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const initializedRef = useRef(false);

  // Fetch user role
  const fetchUserRole = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        setUserRole(null);
        setIsAdmin(false);
        return false;
      }
      
      if (data) {
        setUserRole(data.role);
        const adminStatus = data.role === 'admin' || data.role === 'staff';
        setIsAdmin(adminStatus);
        return adminStatus;
      } else {
        setUserRole(null);
        setIsAdmin(false);
        return false;
      }
    } catch (error) {
      setUserRole(null);
      setIsAdmin(false);
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      // Prevent double initialization
      if (initializedRef.current) return;
      initializedRef.current = true;
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!isMounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      setIsAuthenticated(!!session);
      
      if (session?.user) {
        await fetchUserRole(session.user.id);
      }
      
      if (isMounted) {
        setLoading(false);
      }
    };

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session);
        
        // Only handle role fetch for non-initial events
        if (event !== 'INITIAL_SESSION') {
          if (session?.user) {
            // Defer to avoid deadlock
            setTimeout(async () => {
              if (!isMounted) return;
              await fetchUserRole(session.user.id);
            }, 0);
          } else {
            setUserRole(null);
            setIsAdmin(false);
          }
        }
      }
    );

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        setUser(data.user);
        setSession(data.session);
        setIsAuthenticated(true);
        // Attendi il caricamento del ruolo prima di consentire la navigazione,
        // così ProtectedRoute non reindirizza alla home.
        await fetchUserRole(data.user.id);
        return { success: true };
      }

      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setIsAuthenticated(false);
      setUserRole(null);
      setIsAdmin(false);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error logging out:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, login, logout, user, session, userRole, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};